import {
  Box,
  Button,
  List,
  MediumIcon,
  PopoverMenu,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  CreditNoteSerialSequenceFragment,
  QuoteSerialSequenceFragment,
  SerialSequenceFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { getLastNumber, isSeriesAtMaximum } from 'modules/finance/lib/serialSequenceUtils'

export type SequenceItem =
  | SerialSequenceFragment
  | CreditNoteSerialSequenceFragment
  | QuoteSerialSequenceFragment

interface Props {
  sequence: SequenceItem
  onEditName: () => void
  onArchive: () => void
  onUnarchive: () => void
  onDelete: () => void
}

function getSecondaryAction(sequence: SequenceItem): {
  label: string
  icon: MediumIcon
  critical: boolean
} | null {
  if (sequence.archivedAt) {
    if (sequence.lastGeneratedSerial) {
      const match = sequence.pattern.match(/\{(0+)\}/)
      if (match && match[1]) {
        const maxDigits = match[1].length
        const lastNumber = getLastNumber(sequence.pattern, sequence.lastGeneratedSerial)
        const nextNumber = lastNumber + 1

        if (isSeriesAtMaximum(nextNumber, maxDigits)) {
          return null
        }
      }
    }

    return {
      label: i18n.t('common.unarchive'),
      icon: 'upload',
      critical: false,
    }
  }
  if (sequence.lastGeneratedSerial) {
    return {
      label: i18n.t('common.archive'),
      icon: 'archive',
      critical: false,
    }
  }
  return {
    label: i18n.t('common.delete'),
    icon: 'delete',
    critical: true,
  }
}

export const ListItemSerialSequence = ({
  sequence,
  onEditName,
  onArchive,
  onUnarchive,
  onDelete,
}: Props) => {
  const { company } = useCurrentSession()
  const legalEntity = company.legalEntities.find((le) => le.id === sequence.legalEntityId)
  const secondaryAction = getSecondaryAction(sequence)

  const handleSecondaryAction = () => {
    if (!secondaryAction) return

    if (secondaryAction.label === i18n.t('common.archive')) {
      onArchive()
    } else if (secondaryAction.label === i18n.t('common.unarchive')) {
      onUnarchive()
    } else if (secondaryAction.label === i18n.t('common.delete')) {
      onDelete()
    }
  }

  const totalGeneratedSerials = sequence.lastGeneratedSerial
    ? getLastNumber(sequence.pattern, sequence.lastGeneratedSerial) - sequence.startFrom + 1
    : 0

  const isCreditNote = sequence.__typename === 'FinanceCreditNoteSequence'
  const isQuote = sequence.__typename === 'FinanceQuoteSequence'

  return (
    <List.Item>
      <Box flexDirection="column" gap="s4">
        <Text.Normal weight="semibold">{sequence.name}</Text.Normal>
        <Text.Normal>{sequence.humanReadablePreview}</Text.Normal>
        <Text.Normal size="100">{legalEntity?.legalName}</Text.Normal>
        <Box flexDirection="row" gap="s8">
          <Tag type={isCreditNote ? 'radical' : 'viridian'}>
            {isCreditNote
              ? i18n.t('finance.serial_sequences.list.type_credit_note')
              : isQuote
                ? i18n.t('finance.serial_sequences.list.type_quote')
                : i18n.t('finance.serial_sequences.list.type_invoice')}
          </Tag>
          {totalGeneratedSerials > 0 && (
            <Tag>
              {isQuote
                ? i18n.t('finance.serial_sequences.list.quotes', { count: totalGeneratedSerials })
                : i18n.t('finance.serial_sequences.list.invoices', {
                    count: totalGeneratedSerials,
                  })}
            </Tag>
          )}
          {sequence.archivedAt && <Tag type="champagne">{i18n.t('common.archived')}</Tag>}
        </Box>
      </Box>
      <Box flexDirection="column" justifyContent="spaceBetween" height="full">
        <Box flexDirection="column" justifyContent="center" height="full" alignItems="flexEnd">
          <PopoverMenu type="list">
            <PopoverMenu.Trigger asChild>
              <Button iconName="ellipsis" type="secondary" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="end">
              <PopoverMenu.TouchableOption
                title={i18n.t('common.edit')}
                icon="pencil"
                onClick={onEditName}
                extraContent={null}
              />
              {secondaryAction && (
                <PopoverMenu.TouchableOption
                  title={secondaryAction.label}
                  icon={secondaryAction.icon}
                  danger={secondaryAction.critical}
                  onClick={handleSecondaryAction}
                  extraContent={null}
                />
              )}
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      </Box>
    </List.Item>
  )
}
